import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/revionx-logo.png";
import { trackCTAClick } from "@/lib/ga4";
import { Link } from "react-router-dom";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    trackCTAClick(`Nav Click - ${id}`, "navigation");
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Science", id: "science" },
    { label: "Benefits", id: "benefits" },
    { label: "Reviews", id: "reviews" },
    { label: "FAQ", id: "faq" },
  ];

  const handleMobileMenuClose = () => setIsMobileMenuOpen(false);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-background/95 backdrop-blur-lg border-b border-border shadow-soft" : "bg-background/80 backdrop-blur-sm"
        }`}
      >
        <div className="container px-4">
          <div className="flex items-center justify-between h-14 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img src={logo} alt="REVIONX" className="h-7 md:h-10" width="28" height="28" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/manage-subscription"
                className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
              >
                My Subscription
              </Link>
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden md:block">
              <Button
                onClick={() => scrollToSection("pricing")}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold shadow-blue transition-all hover:shadow-[0_4px_20px_hsl(221_91%_58%/0.4)]"
              >
                <Zap className="w-4 h-4 mr-1.5" />
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-foreground rounded-lg hover:bg-secondary transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-background pt-16 md:hidden"
          >
            <div className="container px-4 py-6">
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-base font-medium text-foreground hover:text-accent transition-colors text-left py-3 px-4 rounded-xl hover:bg-secondary"
                  >
                    {link.label}
                  </button>
                ))}
                <Link
                  to="/manage-subscription"
                  onClick={handleMobileMenuClose}
                  className="text-base font-medium text-foreground hover:text-accent transition-colors text-left py-3 px-4 rounded-xl hover:bg-secondary"
                >
                  My Subscription
                </Link>
                <div className="mt-4 pt-4 border-t border-border">
                  <Button
                    onClick={() => scrollToSection("pricing")}
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold py-6 shadow-blue rounded-xl"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Start My Transformation
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;